// BUILD-042 employee lifecycle (migration 208) - the "Onboarding & Offboarding" section of an employee's workspace.
// Authority: docs/superpowers/decisions/2026-10-22-build-042-local-offboarding-implementation.md, and the Owner-approved layout in
// docs/superpowers/decisions/2026-10-23-build-042-approved-local-visual-implementation.md (PRESENTATION ONLY: no handler, rule or permission changed).
//
// What this section is, and is not:
//   * Two local tabs, Onboarding and Offboarding, inside the existing employee workspace. The left navigation is untouched.
//   * The OFFBOARDING tab SCHEDULES a departure (last working day + exit checklist), CANCELS it, ARCHIVES / RESTORES the person on the Employee list, and
//     REHIRES into a new employment period. Every action re-authorizes on the server (hr.lifecycle.manage, company); a caller without it gets a 404 on the view
//     and the lifecycle cards are then not shown at all (the checklist card still is).
//   * Employment is shown as ACTIVE through the last working day ("Departure scheduled") and as SEPARATED only after it. The server decides that from
//     the Phoenix calendar date; this screen only reports it.
//   * It never changes anyone's account. Account status is shown read-only with a link to Settings. There is no "Complete offboarding" action.
//   * It never cancels a document, completes a task or marks equipment returned. Equipment is returned ONLY through the existing "Equipment returned"
//     step in Documents (actual date, receiving HR person, identified equipment, whole-issuance acknowledgment); this section links there.
//   * Archive / restore change the default Employee list view only.
//   * Loading or unavailable data is never drawn as "nothing outstanding": a failed load says so and offers Try again.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  getHrEmployeeLifecycle, startHrOffboarding, cancelHrOffboarding, archiveHrEmployee, restoreHrEmployee, rehireHrEmployee,
  listHrChecklistTemplates, listHrDepartments, listHrChecklistAssignmentsForEmployee,
  type HrEmployeeLifecycle, type HrChecklistTemplate, type HrDepartment,
} from '../../../api/client';
import { EmployeePicker } from '../EmployeePicker';
import { CollapsibleHelp } from '../CollapsibleHelp';
import { formatLocalDate } from '../hrFormat';

const inputCls = 'border border-gray-300 rounded px-3 py-1.5 text-sm bg-white';
const primaryBtn = 'text-sm font-medium bg-[#4AABDE] text-white rounded px-3 py-1.5 hover:bg-[#3b96c4] disabled:opacity-50 disabled:cursor-not-allowed';
const secondaryBtn = 'text-sm font-medium border border-gray-300 text-gray-700 rounded px-3 py-1.5 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed';
const cardCls = 'bg-white rounded-lg border border-gray-200 shadow-sm p-5';
const cardTitleCls = 'text-base font-semibold text-gray-900';
const pillBase = 'text-xs font-medium border rounded-full px-2.5 py-0.5';

const STATE_COPY: Record<HrEmployeeLifecycle['employment']['state'], { label: string; tone: string }> = {
  active: { label: 'Employed', tone: 'bg-green-50 text-green-800 border-green-200' },
  departure_scheduled: { label: 'Departure scheduled', tone: 'bg-amber-50 text-amber-900 border-amber-300' },
  separated: { label: 'Employment ended', tone: 'bg-gray-100 text-gray-700 border-gray-300' },
  no_open_period: { label: 'Not currently employed', tone: 'bg-gray-100 text-gray-700 border-gray-300' },
};
const RECEIPT_COPY: Record<string, string> = {
  awaiting_return: 'Receipt not signed yet', awaiting_hr_review: 'Receipt signed, waiting for HR review', needs_correction: 'Receipt needs correction',
  reviewed_complete: 'Receipt reviewed - ready to record the return', cancelled: 'Receipt document cancelled - no return recorded',
};
const DOC_STATUS_COPY: Record<string, string> = { awaiting_return: 'Waiting on the employee', awaiting_hr_review: 'Waiting for HR review', needs_correction: 'Needs correction' };

export type OnboardingOffboardingTab = 'onboarding' | 'offboarding';

/** Small inline icons (decorative; the row title carries the meaning). */
function Icon({ kind }: { kind: 'tasks' | 'documents' | 'equipment' | 'external' | 'empty' }) {
  const common = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.7, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true };
  if (kind === 'tasks') return <svg viewBox="0 0 24 24" className="w-5 h-5" {...common}><rect x="4" y="4" width="16" height="16" rx="3" /><path d="M8.5 12.5l2.5 2.5 4.5-5" /></svg>;
  if (kind === 'documents') return <svg viewBox="0 0 24 24" className="w-5 h-5" {...common}><path d="M7 3h7l4 4v14H7z" /><path d="M14 3v4h4" /><path d="M10 12h5M10 16h5" /></svg>;
  if (kind === 'equipment') return <svg viewBox="0 0 24 24" className="w-5 h-5" {...common}><rect x="5" y="5" width="14" height="10" rx="1.5" /><path d="M3 19h18" /></svg>;
  if (kind === 'empty') return <svg viewBox="0 0 24 24" className="w-8 h-8" {...common}><path d="M7 3h7l4 4v14H7z" /><path d="M14 3v4h4" /><path d="M10 13h5M10 17h3" /></svg>;
  return <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 inline-block" {...common}><path d="M14 4h6v6" /><path d="M20 4l-9 9" /><path d="M18 14v5a1 1 0 01-1 1H5a1 1 0 01-1-1V7a1 1 0 011-1h5" /></svg>;
}
export const WorkspaceIcon = Icon;

/** Link to the EXISTING Checklist templates administration, carrying the employee so the way back is one click. */
export function ManageChecklistTemplatesLink({ employeeId, employeeName, tab, className }: { employeeId: string; employeeName: string; tab: OnboardingOffboardingTab; className?: string }) {
  return (
    <Link
      to="/hr/admin/checklists"
      state={{ returnTo: `/hr/admin/employees/${encodeURIComponent(employeeId)}?section=checklists&tab=${tab}`, employeeName }}
      className={className ?? 'text-sm text-[#176A9B] hover:underline'}
      data-testid="manage-checklist-templates"
    >
      Manage checklist templates →
    </Link>
  );
}

/**
 * The tabbed section. `renderChecklist(kind)` supplies the existing, type-specific checklist assignment card for that tab (assign + progress), so the
 * assignment and completion behavior is exactly the existing one. `focusTaskId` (from an attention link) opens the tab that holds that task.
 */
export function OnboardingOffboardingWorkspace({ employeeId, employeeName, initialTab, focusTaskId, onChanged, renderChecklist }: {
  employeeId: string; employeeName: string; initialTab?: OnboardingOffboardingTab; focusTaskId?: string | null; onChanged?: () => void;
  renderChecklist: (kind: OnboardingOffboardingTab) => ReactNode;
}) {
  const [tab, setTab] = useState<OnboardingOffboardingTab>(initialTab ?? 'onboarding');
  const [resolving, setResolving] = useState(!!focusTaskId);

  useEffect(() => { if (initialTab) setTab(initialTab); }, [initialTab]);
  // Oct 24: the employee rail's Onboarding / Offboarding items read the existing ?tab= parameter, so a tab chosen HERE is written back to the URL
  // (replace, only after the first render) and the rail never disagrees with the tab on screen. Presentation only.
  const [, setSearchParams] = useSearchParams();
  const routeState = useLocation().state; // kept on the replace below: a "return to setup" link must survive the tab write-back
  const tabSyncSkipFirst = useRef(true);
  useEffect(() => {
    if (tabSyncSkipFirst.current) { tabSyncSkipFirst.current = false; return; }
    if (new URLSearchParams(window.location.search).get('tab') === tab) return; // already in the URL: nothing to write
    setSearchParams((p) => { if (p.get('tab') === tab) return p; const n = new URLSearchParams(p); n.set('tab', tab); return n; }, { replace: true, state: routeState });
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps -- runs only when the tab changes (setSearchParams changes identity with every URL change)
  useEffect(() => {
    if (!focusTaskId) { setResolving(false); return; }
    let cancelled = false;
    setResolving(true);
    // Open the tab that holds the linked task. A failed lookup just leaves the default tab: nothing is hidden by it.
    listHrChecklistAssignmentsForEmployee(employeeId)
      .then((d) => { if (cancelled) return; const hit = d.assignments.find((a) => a.assignmentId === focusTaskId || a.tasks.some((t) => t.assignmentTaskId === focusTaskId)); if (hit) setTab(hit.processKindSnapshot); })
      .catch(() => { /* default tab */ })
      .finally(() => { if (!cancelled) setResolving(false); });
    return () => { cancelled = true; };
  }, [employeeId, focusTaskId]);

  const tabClass = (t: OnboardingOffboardingTab) =>
    `text-sm pb-2 px-0.5 border-b-2 -mb-px whitespace-nowrap ${tab === t ? 'text-[#176A9B] border-[#4AABDE] font-semibold' : 'text-gray-500 border-transparent hover:text-gray-700'}`;
  function onKey(e: React.KeyboardEvent) {
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); setTab((t) => (t === 'onboarding' ? 'offboarding' : 'onboarding')); }
  }

  return (
    <div className="max-w-[1120px]" data-testid="onboarding-offboarding-workspace">
      <div role="tablist" aria-label="Onboarding and offboarding" className="flex flex-wrap gap-x-6 gap-y-1 border-b border-gray-200" onKeyDown={onKey}>
        <button type="button" role="tab" id="oo-tab-onboarding" aria-selected={tab === 'onboarding'} aria-controls="oo-panel" tabIndex={tab === 'onboarding' ? 0 : -1} className={tabClass('onboarding')} onClick={() => setTab('onboarding')} data-testid="tab-onboarding">Onboarding</button>
        <button type="button" role="tab" id="oo-tab-offboarding" aria-selected={tab === 'offboarding'} aria-controls="oo-panel" tabIndex={tab === 'offboarding' ? 0 : -1} className={tabClass('offboarding')} onClick={() => setTab('offboarding')} data-testid="tab-offboarding">Offboarding</button>
      </div>
      <p className="text-sm text-gray-500 mt-3 mb-4">{tab === 'onboarding' ? 'Assign and follow the onboarding checklist for this employee.' : 'Manage departure tasks and outstanding items.'}</p>

      <div role="tabpanel" id="oo-panel" aria-labelledby={`oo-tab-${tab}`}>
        {resolving ? <div className={`${cardCls} text-sm text-gray-400`}>Loading…</div>
          : tab === 'onboarding'
            ? <div className="space-y-4" data-testid="onboarding-panel">{renderChecklist('onboarding')}</div>
            : <OffboardingSection employeeId={employeeId} employeeName={employeeName} onChanged={onChanged} checklist={renderChecklist('offboarding')} />}
      </div>
    </div>
  );
}

export function OffboardingSection({ employeeId, employeeName, onChanged, checklist }: { employeeId: string; employeeName: string; onChanged?: () => void; checklist?: ReactNode }) {
  const navigate = useNavigate();
  const [data, setData] = useState<HrEmployeeLifecycle | null>(null);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'hidden' | 'error'>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = () => {
    getHrEmployeeLifecycle(employeeId)
      .then((d) => { setData(d); setLoadState('ready'); setLoadError(null); })
      .catch((e: Error & { status?: number }) => {
        // 404 = the caller does not hold lifecycle authority: this surface is simply not present for them (same convention as the rest of HR Administration).
        if (e.status === 404 || e.status === 403) { setLoadState('hidden'); return; }
        // Anything else is a real failure: say so, keep what is already on screen, and offer Try again. Never show an empty "all clear".
        setLoadError(e.message || 'The offboarding information could not be loaded.');
        setLoadState((s) => (s === 'ready' ? 'ready' : 'error'));
      });
  };
  useEffect(() => { setLoadState('loading'); setData(null); setNotice(null); load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [employeeId]);

  if (loadState === 'hidden') return <div className="space-y-4 mb-6">{checklist}</div>;
  if (loadState === 'loading') return <div className="space-y-4 mb-6"><div className={`${cardCls} text-sm text-gray-400`} data-testid="offboarding-loading">Loading offboarding…</div>{checklist}</div>;
  if (loadState === 'error' || !data) {
    return (
      <div className="space-y-4 mb-6">
        <div role="alert" className="bg-white rounded-lg border border-red-200 shadow-sm p-5 text-sm text-red-700" data-testid="offboarding-error">
          {loadError} This is not the same as nothing being outstanding.{' '}
          <button type="button" className="underline" onClick={() => { setLoadState('loading'); load(); }}>Try again</button>
        </div>
        {checklist}
      </div>
    );
  }

  const emp = data.employment;
  const stateCopy = STATE_COPY[emp.state];
  const refreshAll = async (message: string) => { setNotice(message); load(); onChanged?.(); };
  const day = (d: string | null | undefined) => formatLocalDate(d ?? null);
  // Cycle 13: the outstanding checklist tasks, separated by the checklist they belong to (process kind), so nothing is hidden and nothing is mislabelled.
  const taskItems = data.outstanding.tasks.visible ? data.outstanding.tasks.items : [];
  const onboardingTasks = taskItems.filter((t) => t.processKind === 'onboarding');
  const offboardingTasks = taskItems.filter((t) => t.processKind === 'offboarding');
  const otherTasks = taskItems.filter((t) => t.processKind !== 'onboarding' && t.processKind !== 'offboarding');
  const nothingOutstanding = data.outstanding.tasks.visible && taskItems.length === 0
    && data.outstanding.documents.visible && data.outstanding.documents.items.length === 0
    && data.outstanding.equipment.visible && data.outstanding.equipment.outstandingCount === 0;

  return (
    <div className="mb-6" data-testid="offboarding-section">
      {loadError && <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 mb-4">The latest refresh failed ({loadError}). What you see may be out of date. <button type="button" className="underline" onClick={load}>Try again</button></div>}
      {notice && <div role="status" data-testid="offboarding-notice" className="text-sm text-gray-800 bg-[#EDF5FC] border border-[#cfe3f5] rounded-md px-3 py-2 mb-4">{notice}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-4 items-start">
        <div className="space-y-4 min-w-0">
          {/* ── Employment & departure ───────────────────────────────────────────────────────────────── */}
          <div className={cardCls} data-testid="employment-card">
            <div className="flex flex-wrap items-center gap-2 mb-1.5">
              <h2 className={cardTitleCls}>Employment &amp; departure</h2>
              <span className={`${pillBase} ${stateCopy.tone}`} data-testid="employment-state">{stateCopy.label}</span>
              {data.archive.archived && <span className={`${pillBase} bg-gray-100 text-gray-700 border-gray-300`} data-testid="archived-badge">Archived</span>}
            </div>
            {emp.state === 'departure_scheduled' && emp.departure && (
              <p className="text-sm text-gray-700" data-testid="departure-line">
                Last working day <strong>{day(emp.departure.lastWorkingDate)}</strong>. {employeeName} stays employed through that day (Arizona time) and employment ends after it. Today is {day(data.todayPhoenix)}.
              </p>
            )}
            {emp.state === 'separated' && emp.departure && (
              <p className="text-sm text-gray-700" data-testid="departure-line">Employment ended after <strong>{day(emp.departure.lastWorkingDate)}</strong>. Their records, documents and history are kept. Rehire opens a new employment period.</p>
            )}
            {emp.state === 'active' && <p className="text-sm text-gray-600">No departure scheduled.{emp.openPeriod ? ` Employed since ${day(emp.openPeriod.hireDate)} (employment period ${emp.openPeriod.periodNumber}).` : ''}</p>}
            {emp.state === 'no_open_period' && <p className="text-sm text-gray-600">There is no open employment period. Rehire opens a new one.</p>}

            {emp.state === 'active' && <ScheduleForm employeeId={employeeId} employeeName={employeeName} onDone={(m) => refreshAll(m)} />}
            {emp.state === 'departure_scheduled' && emp.departure && <CancelForm offboardingId={emp.departure.offboardingId} lastWorkingDate={emp.departure.lastWorkingDate} onDone={(m) => refreshAll(m)} />}
            {(emp.state === 'separated' || emp.state === 'no_open_period') && <RehireForm employeeId={employeeId} employeeName={employeeName} lastWorkingDate={emp.departure?.lastWorkingDate ?? emp.periods[emp.periods.length - 1]?.separationDate ?? null} archived={data.archive.archived} onDone={(m) => refreshAll(m)} />}

            {(emp.periods.length > 1 || emp.departureHistory.length > 0) && (
              <details className="mt-4 text-xs text-gray-600">
                <summary className="cursor-pointer text-[#176A9B]">Employment periods and earlier departures ({emp.periods.length} period{emp.periods.length === 1 ? '' : 's'})</summary>
                <ul className="mt-2 space-y-1">
                  {emp.periods.map((p) => <li key={p.periodNumber}>Period {p.periodNumber}: hired {day(p.hireDate)}{p.separationDate ? `, ended after ${day(p.separationDate)}` : ' (current)'}</li>)}
                  {emp.departureHistory.map((d) => <li key={d.offboardingId}>Departure for {day(d.lastWorkingDate)}: {d.cancelledAt ? `cancelled ${day(d.cancelledAt)}${d.cancelNote ? ` - ${d.cancelNote}` : ''}` : 'took effect'}</li>)}
                </ul>
              </details>
            )}
          </div>

          {/* ── Offboarding checklist (the existing type-specific assignment card, supplied by the caller) ───── */}
          {checklist}

          {/* ── Outstanding items: compact rows ─────────────────────────────────────────────────────────── */}
          <div className={cardCls} data-testid="outstanding-card">
            <h2 className={`${cardTitleCls} mb-1`}>Outstanding items</h2>
            {/* Cycle 13: every unresolved item stays listed, in labelled groups. "Nothing outstanding" only when every group was read and is empty. */}
            {nothingOutstanding && <p className="text-sm text-gray-700 py-2" data-testid="outstanding-none">Nothing outstanding.</p>}
            <div className="divide-y divide-gray-100">
              {([
                { kind: 'onboarding' as const, title: 'Onboarding tasks', items: onboardingTasks },
                { kind: 'offboarding' as const, title: 'Offboarding tasks', items: offboardingTasks },
              ]).map((g) => (
                <OutstandingRow key={g.kind} icon="tasks" title={g.title} testId={`outstanding-group-${g.kind}`}
                  summary={!data.outstanding.tasks.visible ? null : g.items.length === 0 ? 'None outstanding.' : `${g.items.length} open`}>
                  {!data.outstanding.tasks.visible ? <Hidden reason={data.outstanding.tasks.reason} /> : g.items.length > 0 && (
                    <ul className="divide-y divide-gray-100" data-testid={`outstanding-tasks-${g.kind}`}>
                      {g.items.map((t) => (
                        <li key={t.assignmentTaskId} className="py-2 flex flex-wrap items-center justify-between gap-2 text-sm min-w-0">
                          <span className="min-w-0 break-words"><strong>{t.task}</strong> <span className="text-gray-500">· {t.checklist}{t.fromThisDeparture ? ' (this departure)' : ''} · {t.responsibleRole} · due {day(t.dueDate)} · {t.status.replace('_', ' ')}</span></span>
                          <Link className="text-xs text-[#176A9B] hover:underline max-w-full text-left" data-testid="outstanding-task-link"
                            to={`/hr/admin/employees/${encodeURIComponent(employeeId)}?section=checklists&tab=${g.kind}&item=${encodeURIComponent(t.assignmentTaskId)}`}>
                            {g.kind === 'onboarding' ? 'Open in Onboarding →' : 'Open in Offboarding →'}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </OutstandingRow>
              ))}
              {otherTasks.length > 0 && (
                <OutstandingRow icon="tasks" title="Other checklist tasks" testId="outstanding-group-other" summary={`${otherTasks.length} open · checklist type not reported`}>
                  <ul className="divide-y divide-gray-100" data-testid="outstanding-tasks-other">
                    {otherTasks.map((t) => (
                      <li key={t.assignmentTaskId} className="py-2 text-sm min-w-0 break-words"><strong>{t.task}</strong> <span className="text-gray-500">· {t.checklist} · {t.responsibleRole} · due {day(t.dueDate)}</span></li>
                    ))}
                  </ul>
                </OutstandingRow>
              )}

              <OutstandingRow icon="documents" title="Documents" testId="outstanding-group-documents"
                summary={!data.outstanding.documents.visible ? null : data.outstanding.documents.items.length === 0 ? 'None outstanding.' : `${data.outstanding.documents.items.length} open`}>
                {!data.outstanding.documents.visible ? <Hidden reason={data.outstanding.documents.reason} /> : data.outstanding.documents.items.length > 0 && (
                  <ul className="divide-y divide-gray-100" data-testid="outstanding-documents">
                    {data.outstanding.documents.items.map((d) => (
                      <li key={d.assignmentId} className="py-2 flex flex-wrap items-center justify-between gap-2 text-sm min-w-0">
                        <span className="min-w-0 break-words"><strong>{d.form}</strong> <span className="text-gray-500">· version {d.versionNumber} · {DOC_STATUS_COPY[d.status] ?? d.status}{d.signatureRequestPending ? ' · signature request still pending (left as is)' : ''}</span></span>
                        <button type="button" className="text-xs text-[#176A9B] hover:underline max-w-full text-left" onClick={() => navigate(`/hr/admin/employees/${employeeId}?section=documents&item=${d.assignmentId}`)}>Open in Documents →</button>
                      </li>
                    ))}
                  </ul>
                )}
              </OutstandingRow>

              <OutstandingRow icon="equipment" title="Equipment" testId="outstanding-group-equipment"
                summary={!data.outstanding.equipment.visible ? null : data.outstanding.equipment.items.length === 0 ? 'None issued.' : `${data.outstanding.equipment.outstandingCount} not recorded as returned · ${data.outstanding.equipment.returnedCount} returned`}>
                {!data.outstanding.equipment.visible ? <Hidden reason={data.outstanding.equipment.reason} /> : data.outstanding.equipment.items.length > 0 && (
                  <div data-testid="outstanding-equipment">
                    <ul className="divide-y divide-gray-100">
                      {data.outstanding.equipment.items.map((i) => (
                        <li key={i.itemKey} className="py-2 text-sm flex flex-wrap items-center justify-between gap-2 min-w-0" data-testid={`equipment-${i.returned ? 'returned' : 'outstanding'}`}>
                          <span className="min-w-0 break-words">
                            <strong>{i.equipmentType}</strong> - {i.description}{i.serialOrAsset ? ` (${i.serialOrAsset})` : ''}
                            <span className="block text-xs text-gray-500">
                              {i.returned
                                ? `Returned ${day(i.returned.returnDate)} · received by ${i.returned.receivedBy ?? 'not recorded'}`
                                : `Outstanding · ${RECEIPT_COPY[i.receiptState] ?? i.receiptState}`}
                            </span>
                          </span>
                          {!i.returned && (
                            <button type="button" className={`max-w-full text-left ${i.canRecordReturn ? 'text-xs text-[#176A9B] hover:underline' : 'text-xs text-gray-400 cursor-default'}`} disabled={!i.canRecordReturn}
                              title={i.canRecordReturn ? undefined : 'A return can be recorded once the receipt is reviewed and complete.'}
                              onClick={() => navigate(`/hr/admin/employees/${employeeId}?section=documents&item=${i.assignmentId}`)}>
                              {i.canRecordReturn ? 'Record the return in Documents →' : 'Receipt must be reviewed first'}
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                    <details className="mt-2 text-xs text-gray-500"><summary className="cursor-pointer text-[#176A9B]">How equipment returns are counted</summary>
                      <ul className="mt-1 list-disc pl-5 space-y-0.5">{data.outstanding.equipment.rules.map((r) => <li key={r}>{r}</li>)}</ul>
                    </details>
                  </div>
                )}
              </OutstandingRow>
            </div>
          </div>
        </div>

        {/* ── Supporting cards ─────────────────────────────────────────────────────────────────────────── */}
        <aside className="space-y-4 min-w-0" aria-label="Account access and Employee list">
          <AccountCard data={data} onOpen={(path) => navigate(path)} />
          <ArchiveCard employeeId={employeeId} employeeName={employeeName} archived={data.archive.archived} lastEvent={data.archive.events[0] ?? null} onDone={(m) => refreshAll(m)} />
        </aside>

        <div className="lg:col-span-2">
          <CollapsibleHelp title="How offboarding works">
            <ul className="list-disc pl-5 space-y-1">
              <li>Scheduling a departure keeps the person employed through the last working day (Arizona time). Employment ends after it.</li>
              <li>Nothing is closed for you. Cancelling a departure or archiving a person leaves every task, document, signature request and equipment record exactly as it is.</li>
              <li>Account access is changed in Settings by someone with that authority. This page only shows it; restoring, archiving or rehiring does not change it either.</li>
              <li>Equipment is counted as returned only through the “Equipment returned” step in Documents.</li>
              <li>Archiving only hides the person from the default Employees list. It deletes nothing.</li>
            </ul>
          </CollapsibleHelp>
        </div>
      </div>
    </div>
  );
}

function OutstandingRow({ icon, title, summary, children, testId }: { icon: 'tasks' | 'documents' | 'equipment'; title: string; summary: string | null; children?: ReactNode; testId?: string }) {
  return (
    <div className="py-3 first:pt-2 last:pb-1" data-testid={testId}>
      <div className="flex items-start gap-3">
        <span className="flex-shrink-0 w-9 h-9 rounded-full bg-[#EDF5FC] text-[#176A9B] flex items-center justify-center"><Icon kind={icon} /></span>
        <div className="min-w-0 flex-1 break-words">
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          {summary && <p className="text-sm text-gray-500">{summary}</p>}
          {children && <div className="mt-1">{children}</div>}
        </div>
      </div>
    </div>
  );
}

function Hidden({ reason }: { reason: string }) {
  return <p className="text-sm text-gray-500" data-testid="section-hidden">Not shown to you. {reason}</p>;
}

function AccountCard({ data, onOpen }: { data: HrEmployeeLifecycle; onOpen: (path: string) => void }) {
  const a = data.account;
  const enabled = a.available && a.globalStatus === 'active' && a.uniquehqAllowed;
  const known = a.available && a.globalStatus !== null;
  return (
    <div className={cardCls} data-testid="account-card">
      <div className="flex flex-wrap items-center gap-2 mb-1.5">
        <h2 className={cardTitleCls}>Account access</h2>
        {a.available && <span className={`${pillBase} ${!known ? 'bg-gray-100 text-gray-700 border-gray-300' : enabled ? 'bg-green-50 text-green-800 border-green-200' : 'bg-gray-100 text-gray-700 border-gray-300'}`} data-testid="account-status">{!known ? 'Status unknown' : enabled ? 'Access enabled' : 'Access off'}</span>}
      </div>
      {a.available ? (
        <>
          <p className="text-sm text-gray-700">
            Managed in Settings. Account is <strong>{a.globalStatus === 'disabled' ? 'turned off' : a.globalStatus === 'active' ? 'on' : 'in an unknown state'}</strong>; UniqueHQ access is <strong>{a.uniquehqAllowed ? 'granted' : 'not granted'}</strong>. Shown for information only.
          </p>
          <p className="text-xs text-gray-500 mt-1">{a.changeNote}</p>
          <button type="button" className="mt-3 text-sm text-[#176A9B] hover:underline" onClick={() => onOpen(a.settingsPath || '/settings')} data-testid="manage-uniquehq-access">Manage UniqueHQ access →</button>
        </>
      ) : <p className="text-sm text-gray-600">Account status could not be read right now ({a.reason}). Nothing about access is changed from this page.</p>}
    </div>
  );
}

function ScheduleForm({ employeeId, employeeName, onDone }: { employeeId: string; employeeName: string; onDone: (message: string) => void }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [note, setNote] = useState('');
  const [templates, setTemplates] = useState<HrChecklistTemplate[] | null>(null);
  const [templatesError, setTemplatesError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!open || templates !== null) return;
    listHrChecklistTemplates().then((d) => { setTemplates(d.templates.filter((t) => t.processKind === 'offboarding' && !t.retiredAt)); setTemplatesError(false); }).catch(() => { setTemplates([]); setTemplatesError(true); });
  }, [open, templates]);
  async function submit() {
    if (busy || !date) return;
    setBusy(true); setError(null);
    try {
      await startHrOffboarding(employeeId, { lastWorkingDate: date, checklistTemplateId: templateId || null, note: note.trim() || null });
      setOpen(false); setDate(''); setTemplateId(''); setNote('');
      onDone(`Departure scheduled for ${employeeName}. Employment continues through the last working day. Nothing about their account, documents or equipment was changed.`);
    } catch (e: any) {
      // Typed values stay exactly as they were.
      setError(`Not scheduled. ${e?.message || 'The departure could not be saved.'} Your entries are still here.`);
    } finally { setBusy(false); }
  }
  if (!open) return <button type="button" className={`${primaryBtn} mt-3`} onClick={() => setOpen(true)} data-testid="schedule-open">Schedule a departure</button>;
  return (
    <div className="mt-3 border border-gray-200 rounded-md p-4 bg-gray-50" data-testid="schedule-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="text-xs text-gray-600 flex flex-col gap-1">Last working day
          <input type="date" className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} data-testid="lwd-input" />
        </label>
        <label className="text-xs text-gray-600 flex flex-col gap-1">Exit checklist
          <select className={inputCls} value={templateId} onChange={(e) => setTemplateId(e.target.value)} data-testid="template-select">
            <option value="">No checklist yet (add one later)</option>
            {(templates ?? []).map((t) => <option key={t.templateId} value={t.templateId}>{t.name}</option>)}
          </select>
        </label>
      </div>
      {templates === null && <p className="text-xs text-gray-400 mt-1">Loading checklists…</p>}
      {templatesError && <p className="text-xs text-red-700 mt-1">Exit checklists could not be loaded. You can still schedule the departure and add a checklist afterward.</p>}
      {templates !== null && !templatesError && templates.length === 0 && (
        <p className="text-xs text-gray-500 mt-1">No offboarding checklist templates exist yet. <ManageChecklistTemplatesLink employeeId={employeeId} employeeName={employeeName} tab="offboarding" className="text-xs text-[#176A9B] hover:underline" /> to create one, or schedule without one.</p>
      )}
      <label className="text-xs text-gray-600 flex flex-col gap-1 mt-3">Note (optional)
        <input className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
      </label>
      <p className="text-xs text-gray-500 mt-2">{employeeName} stays employed through the last working day. Scheduling does not change their account, cancel any document or mark any equipment returned.</p>
      {error && <p role="alert" className="text-sm text-red-700 mt-2" data-testid="schedule-error">{error}</p>}
      <div className="flex gap-2 mt-3">
        <button type="button" className={primaryBtn} disabled={busy || !date} onClick={submit}>{busy ? 'Scheduling…' : 'Schedule departure'}</button>
        <button type="button" className={secondaryBtn} disabled={busy} onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

function CancelForm({ offboardingId, lastWorkingDate, onDone }: { offboardingId: string; lastWorkingDate: string; onDone: (message: string) => void }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function submit() {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      await cancelHrOffboarding(offboardingId, { note: note.trim() || null });
      setOpen(false); setNote('');
      onDone('Departure cancelled. The exit checklist, its history, documents, signature requests and equipment records were left exactly as they were.');
    } catch (e: any) { setError(`Not cancelled. ${e?.message || 'The departure could not be cancelled.'}`); }
    finally { setBusy(false); }
  }
  if (!open) return <button type="button" className={`${secondaryBtn} mt-3`} onClick={() => setOpen(true)} data-testid="cancel-open">Cancel this departure</button>;
  return (
    <div className="mt-3 border border-gray-200 rounded-md p-4 bg-gray-50" data-testid="cancel-form">
      <p className="text-sm text-gray-700">Cancel the departure planned for {formatLocalDate(lastWorkingDate)}? The record of it, who cancelled it and why is kept.</p>
      <label className="text-xs text-gray-600 flex flex-col gap-1 mt-2">Reason (optional)
        <input className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
      </label>
      {error && <p role="alert" className="text-sm text-red-700 mt-2">{error}</p>}
      <div className="flex gap-2 mt-3">
        <button type="button" className={primaryBtn} disabled={busy} onClick={submit}>{busy ? 'Cancelling…' : 'Cancel departure'}</button>
        <button type="button" className={secondaryBtn} disabled={busy} onClick={() => setOpen(false)}>Keep it</button>
      </div>
    </div>
  );
}

function RehireForm({ employeeId, employeeName, lastWorkingDate, archived, onDone }: { employeeId: string; employeeName: string; lastWorkingDate: string | null; archived: boolean; onDone: (message: string) => void }) {
  const [open, setOpen] = useState(false);
  const [hireDate, setHireDate] = useState('');
  const [title, setTitle] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [managerId, setManagerId] = useState('');
  const [departments, setDepartments] = useState<HrDepartment[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (open) listHrDepartments().then((d) => setDepartments(d.departments)).catch(() => setDepartments([])); }, [open]);
  async function submit() {
    if (busy || !hireDate) return;
    setBusy(true); setError(null);
    try {
      await rehireHrEmployee(employeeId, { hireDate, title: title.trim() || null, departmentId: departmentId || null, managerEmployeeId: managerId || null });
      setOpen(false);
      onDone(`${employeeName} was rehired into a new employment period. Their earlier period and history are kept. Their account was not changed: turning access back on is a separate step in Settings.`);
    } catch (e: any) { setError(`Not rehired. ${e?.message || 'The rehire could not be saved.'} Your entries are still here.`); }
    finally { setBusy(false); }
  }
  if (archived) return <p className="text-xs text-gray-600 mt-3" data-testid="rehire-blocked">To rehire {employeeName}, first restore them to the Employee list (a separate step), then rehire.</p>;
  if (!open) return <button type="button" className={`${secondaryBtn} mt-3`} onClick={() => setOpen(true)} data-testid="rehire-open">Rehire into a new employment period</button>;
  return (
    <div className="mt-3 border border-gray-200 rounded-md p-4 bg-gray-50" data-testid="rehire-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="text-xs text-gray-600 flex flex-col gap-1">New hire date{lastWorkingDate ? ` (after ${formatLocalDate(lastWorkingDate)})` : ''}
          <input type="date" className={inputCls} value={hireDate} onChange={(e) => setHireDate(e.target.value)} data-testid="rehire-date" />
        </label>
        <label className="text-xs text-gray-600 flex flex-col gap-1">Title
          <input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label className="text-xs text-gray-600 flex flex-col gap-1">Department
          <select className={inputCls} value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
            <option value="">None</option>
            {departments.map((d) => <option key={d.departmentId} value={d.departmentId}>{d.name}</option>)}
          </select>
        </label>
        <div className="text-xs text-gray-600 flex flex-col gap-1">Manager
          <EmployeePicker value={managerId} onChange={setManagerId} placeholder="Manager (optional)" />
        </div>
      </div>
      <p className="text-xs text-gray-500 mt-2">This opens a new employment period. The earlier period, its terms, documents and history stay as they are. It does not turn their account back on.</p>
      {error && <p role="alert" className="text-sm text-red-700 mt-2">{error}</p>}
      <div className="flex gap-2 mt-3">
        <button type="button" className={primaryBtn} disabled={busy || !hireDate} onClick={submit}>{busy ? 'Rehiring…' : 'Rehire'}</button>
        <button type="button" className={secondaryBtn} disabled={busy} onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

function ArchiveCard({ employeeId, employeeName, archived, lastEvent, onDone }: {
  employeeId: string; employeeName: string; archived: boolean; lastEvent: { occurredAt: string; note: string | null } | null; onDone: (message: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function submit() {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      if (archived) await restoreHrEmployee(employeeId, { note: note.trim() || null }); else await archiveHrEmployee(employeeId, { note: note.trim() || null });
      setOpen(false); setNote('');
      onDone(archived
        ? `${employeeName} is back on the default Employee list. Only the Employee list changed.`
        : `${employeeName} is archived from the default Employee list. Only the Employee list changed: employment, documents, signatures, checklists, equipment records and account access are exactly as they were. Find them again with “Show archived” on the Employees list.`);
    } catch (e: any) { setError(`${archived ? 'Not restored' : 'Not archived'}. ${e?.message || 'The change could not be saved.'}`); }
    finally { setBusy(false); }
  }
  return (
    <div className={cardCls} data-testid="archive-card">
      <div className="flex flex-wrap items-center gap-2 mb-1.5">
        <h2 className={cardTitleCls}>Employee list</h2>
        <span className={`${pillBase} ${archived ? 'bg-gray-100 text-gray-700 border-gray-300' : 'bg-green-50 text-green-800 border-green-200'}`} data-testid="listing-status">{archived ? 'Archived' : 'Listed'}</span>
      </div>
      <p className="text-sm text-gray-700">{archived ? `Archived${lastEvent ? ` ${formatLocalDate(lastEvent.occurredAt)}` : ''}${lastEvent?.note ? ` - ${lastEvent.note}` : ''}. Hidden from the default Employees list; still findable with “Show archived”.` : 'Shown on the default Employees list.'}</p>
      <p className="text-xs text-gray-500 mt-1">Archiving is separate from ending employment. It only hides the person from the default Employees list. It deletes nothing, completes or cancels nothing, and never changes account access. Restoring only makes them visible again.</p>
      {!open ? (
        <button type="button" className={`${secondaryBtn} mt-3`} onClick={() => setOpen(true)} data-testid={archived ? 'restore-open' : 'archive-open'}>{archived ? 'Restore to list' : 'Archive from list'}</button>
      ) : (
        <div className="mt-3 border border-gray-200 rounded-md p-4 bg-gray-50">
          <label className="text-xs text-gray-600 flex flex-col gap-1">Note (optional)
            <input className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
          </label>
          {error && <p role="alert" className="text-sm text-red-700 mt-2">{error}</p>}
          <div className="flex gap-2 mt-3">
            <button type="button" className={primaryBtn} disabled={busy} onClick={submit} data-testid="archive-confirm">{busy ? 'Saving…' : archived ? 'Restore' : 'Archive'}</button>
            <button type="button" className={secondaryBtn} disabled={busy} onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
